"use client"

import React from "react"
import Modal from "react-bootstrap/Modal"
import Button from "react-bootstrap/Button"
import { connect } from "react-redux"
import { PostVote } from "./PostVote"
import {
  CalendarDays,
  Clock3,
  Coins,
  FileText,
  Flag,
  ThumbsDown,
  ThumbsUp,
  Users,
} from "lucide-react"
import "../styles/components/CampaignModal.css"

const currencyFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 8,
})

const formatDate = (value) => {
  if (!value) return "No deadline"

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "No deadline"

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

const formatCurrency = (value) => {
  const amount = Number(value || 0)
  return amount > 0 ? currencyFormatter.format(amount) : "Not requested"
}

const getDaysRemaining = (value) => {
  if (!value) return "Open-ended"

  const expiration = new Date(value)
  if (Number.isNaN(expiration.getTime())) return "Open-ended"

  const days = Math.ceil((expiration - new Date()) / (1000 * 60 * 60 * 24))
  if (days < 0) return "Expired"
  if (days === 0) return "Closes today"
  return `${days} day${days === 1 ? "" : "s"} left`
}

export const getEligibleMemberCount = (reportedCount, totalVotes) =>
  Math.max(Number(reportedCount || 0), Number(totalVotes || 0))

const InfoTile = ({ icon, label, value, tone = "" }) => (
  <div className={`campaign-info-tile ${tone}`}>
    <span className="campaign-info-icon">{icon}</span>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
)

export const CampaignModal = ({ isOpen, onClose, campaignData, isDarkMode, onVoteSaved }) => {
  if (!campaignData || !campaignData.id) {
    return (
      <Modal className="campaign-modal" show={isOpen} onHide={onClose} contentClassName={isDarkMode ? "dark-mode-modal campaign-modal-content" : "campaign-modal-content"}>
        <Modal.Header closeButton closeVariant={isDarkMode ? "white" : undefined}>
          <Modal.Title>Campaign Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>No campaign data available.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    )
  }

  const upVotes = Number(campaignData.voteUp || 0)
  const downVotes = Number(campaignData.voteDown || 0)
  const totalVotes = upVotes + downVotes
  const totalGroupMembers = getEligibleMemberCount(campaignData.totalGroupMembers, totalVotes)
  const remainingVotes = Math.max(totalGroupMembers - totalVotes, 0)
  const approvalPercentage = totalVotes > 0 ? Math.round((upVotes / totalVotes) * 100) : 0
  const participationPercentage = totalGroupMembers > 0 ? Math.round((totalVotes / totalGroupMembers) * 100) : 0
  const supportShare = totalGroupMembers > 0 ? (upVotes / totalGroupMembers) * 100 : 0
  const opposeShare = totalGroupMembers > 0 ? (downVotes / totalGroupMembers) * 100 : 0
  const neutralShare = Math.max(100 - supportShare - opposeShare, 0)
  const status = campaignData.status || "Active"
  const campaignType = campaignData.campaignType || "Campaign"
  const isFellowshipDecision = campaignType.toLowerCase() === "fellowshipproposal"
  const fundingInitial = formatCurrency(campaignData.fundingInitial)
  const fundingMonthly = formatCurrency(campaignData.fundingMonthlyRecurring)

  return (
    <Modal
      className="campaign-modal"
      show={isOpen}
      onHide={onClose}
      size="xl"
      centered
      dialogClassName="campaign-modal-dialog"
      contentClassName={isDarkMode ? "dark-mode-modal campaign-modal-content" : "campaign-modal-content"}
    >
      <Modal.Header closeButton closeVariant={isDarkMode ? "white" : undefined} className="campaign-modal-header">
        <div>
          <div className="campaign-modal-kicker">
            <Flag size={15} aria-hidden="true" />
            {campaignType}
          </div>
          <Modal.Title>{campaignData.title}</Modal.Title>
        </div>
        <span className={`campaign-status-pill ${status.toLowerCase()}`}>{status}</span>
      </Modal.Header>

      <Modal.Body className="campaign-modal-body">
        <section className="campaign-modal-hero" aria-label="Campaign overview">
          <div className="campaign-story">
            <span className="campaign-section-label">Proposal</span>
            <p>{campaignData.proposal || "No proposal text was provided for this campaign."}</p>
          </div>

          <aside className="campaign-decision-card" aria-label="Voting summary">
            <div className="campaign-decision-ring" style={{ "--approval": `${approvalPercentage}%` }}>
              <div>
                <strong>{approvalPercentage}%</strong>
                <span>approval</span>
              </div>
            </div>
            <div className="campaign-decision-copy">
              <strong>{totalVotes} of {totalGroupMembers} members voted</strong>
              <span>{participationPercentage}% participation · {remainingVotes} remaining</span>
            </div>
          </aside>
        </section>

        {isFellowshipDecision && <section className="campaign-benefit-card">
          <span className="campaign-section-label"><Users size={15} aria-hidden="true" /> Mutual fellowship decision</span>
          <p>This focused decision skips refinement and stays open until the community reaches an outcome. If approved here, the invited congregation considers the same relationship for itself. No fellowship is formed and no church is added until both sides approve.</p>
        </section>}

        <section className="campaign-info-grid" aria-label="Campaign details">
          <InfoTile icon={<CalendarDays size={17} />} label="Created" value={formatDate(campaignData.createdOn)} />
          <InfoTile icon={<Clock3 size={17} />} label="Deadline" value={getDaysRemaining(campaignData.expiration)} tone="deadline" />
          {!isFellowshipDecision && <InfoTile icon={<Coins size={17} />} label="Initial ask" value={fundingInitial} />}
          {!isFellowshipDecision && <InfoTile icon={<Coins size={17} />} label="Monthly ask" value={fundingMonthly} />}
        </section>

        {campaignData.benefitToGroup && (
          <section className="campaign-benefit-card">
            <span className="campaign-section-label">
              <FileText size={15} aria-hidden="true" />
              Why it matters
            </span>
            <p>{campaignData.benefitToGroup}</p>
          </section>
        )}

        <section className="campaign-vote-dashboard" aria-label="Voting results">
          <div className="campaign-results-panel">
            <div className="campaign-results-heading">
              <span className="campaign-section-label">Current results</span>
              <strong>{totalVotes ? `${approvalPercentage}% support among voters` : "Awaiting first vote"}</strong>
            </div>

            <div className="campaign-result-bars">
              <div className="campaign-result-row support">
                <div>
                  <span><ThumbsUp size={15} /> Support</span>
                  <strong>{upVotes}</strong>
                </div>
                <div className="campaign-result-track">
                  <span style={{ width: `${supportShare}%` }} />
                </div>
              </div>
              <div className="campaign-result-row oppose">
                <div>
                  <span><ThumbsDown size={15} /> Oppose</span>
                  <strong>{downVotes}</strong>
                </div>
                <div className="campaign-result-track">
                  <span style={{ width: `${opposeShare}%` }} />
                </div>
              </div>
              <div className="campaign-result-row remaining">
                <div>
                  <span><Users size={15} /> Not yet voted</span>
                  <strong>{remainingVotes}</strong>
                </div>
                <div className="campaign-result-track">
                  <span style={{ width: `${neutralShare}%` }} />
                </div>
              </div>
            </div>
          </div>

          <div className="campaign-cast-vote-card">
            <div className="campaign-cast-heading">
              <span className="campaign-section-label">Cast your vote</span>
              <strong>Your voice moves the campaign forward.</strong>
            </div>
            <PostVote campaignId={campaignData.id} campaignStatus={status} isDarkMode={isDarkMode} onVoteSaved={onVoteSaved} />
          </div>
        </section>

      </Modal.Body>

      <Modal.Footer className="campaign-modal-footer">
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

const mapStateToProps = (state) => ({
  isDarkMode: state.theme.isDarkMode,
})

export default connect(mapStateToProps)(CampaignModal)
